import { PriceProductProps, ProductProps, tableKeys } from '@/types';
import { useContext, createContext, useState, useRef, useCallback, useEffect } from 'react';
import {
  RCA_CATALOG_INVALIDATE_EVENT,
  RCA_CATALOG_PENDING_DEFERRED,
  RCA_CATALOG_PENDING_IMMEDIATE,
  RCA_CATALOG_PENDING_INVALIDATE_SESSION_KEY,
  parseCatalogPendingInvalidateFlag,
  type CatalogInvalidateEventDetail,
} from '@/shared/catalogCacheInvalidate';
import { useTables } from './TablesContext';
import { useLocation } from 'react-router-dom';
import { useClient } from './ClientContext';
import { ECOMMERCE, MAIN } from '@/constants/systemRoutes';

export type Tables = (typeof tableKeys)[number];
/** Catálogo por cliente e escopo de loja+segmento (`activeBrandKey`, ex.: `"12:5"`). */
export type CatalogByClientAndScope = Record<number, Record<string, ProductProps[]>>;

interface LoadingContextType {
  catalog: CatalogByClientAndScope;
  isLoading: React.MutableRefObject<boolean>;
  calculatedList: React.MutableRefObject<Record<string, PriceProductProps[]>>;
  setProducts: (client: number, scopeKey: string, catalog: ProductProps[]) => void;
  isLoaded: React.MutableRefObject<Record<string, boolean>>;
}

const LoadingContext = createContext({} as LoadingContextType);
const MAX_CALCULATED_CACHE_KEYS = 1500;
const MAX_CALCULATED_CACHE_KEYS_PER_CLIENT = 600;
/** Máximo de escopos (loja+segmento) por cliente em cache — troca de bandeira + margem. */
const MAX_CATALOG_SCOPES_LOW_MEMORY = 4;
const MAX_CATALOG_SCOPES_DEFAULT = 12;

const shouldUseLowMemoryCatalogMode = () => {
  if (typeof navigator === 'undefined') return false;

  const nav = navigator as Navigator & { deviceMemory?: number };
  const ua = nav.userAgent || '';
  const isAndroid = /Android/i.test(ua);
  const isOldAndroid = /Android\s(4|5|6)\b/i.test(ua);

  if (typeof nav.deviceMemory === 'number') {
    return nav.deviceMemory <= 2;
  }

  return isAndroid && isOldAndroid;
};

const PricesProvider = ({ children }: React.PropsWithChildren) => {
  const { tables } = useTables();
  const { pathname } = useLocation();
  const { selectedClient } = useClient();
  const [catalog, setHookProducts] = useState<CatalogByClientAndScope>({} as CatalogByClientAndScope);

  const isLoading = useRef<boolean>(false);
  const isLoadingProducts = useRef<boolean>(false);
  const isLoaded = useRef<Record<string, boolean>>({});
  const calculatedList = useRef<Record<string, PriceProductProps[]>>({});
  const lowMemoryCatalogMode = useRef<boolean>(shouldUseLowMemoryCatalogMode());
  const prevClientCodRef = useRef<number | undefined>(undefined);

  const pruneCalculatedCache = useCallback((client: number) => {
    const clientPrefix = `${client}-`;
    const keys = Object.keys(calculatedList.current);
    const clientKeys = keys.filter((key) => key.startsWith(clientPrefix));

    if (clientKeys.length > MAX_CALCULATED_CACHE_KEYS_PER_CLIENT) {
      const extraClientKeys = clientKeys.length - MAX_CALCULATED_CACHE_KEYS_PER_CLIENT;
      for (let i = 0; i < extraClientKeys; i++) {
        delete calculatedList.current[clientKeys[i]];
      }
    }

    const allKeys = Object.keys(calculatedList.current);
    if (allKeys.length > MAX_CALCULATED_CACHE_KEYS) {
      const extraGlobalKeys = allKeys.length - MAX_CALCULATED_CACHE_KEYS;
      for (let i = 0; i < extraGlobalKeys; i++) {
        delete calculatedList.current[allKeys[i]];
      }
    }
  }, []);

  const setProducts = useCallback(
    (client: number, scopeKey: string, newCatalog: ProductProps[]) => {
      setHookProducts((prevCatalog) => {
        if (isLoadingProducts.current) return prevCatalog;

        try {
          isLoadingProducts.current = true;

          const restrictions = (Array.isArray(tables.current.restricaoProdutoCliente)
            ? tables.current.restricaoProdutoCliente
            : []
          ).reduce((acc: Record<number, boolean>, curr) => {
            if (curr.COD_CLIENTE === client) {
              acc[curr.COD_PRODUTO] = true;
            }
            return acc;
          }, {} as Record<number, boolean>);

          const catalogUltimaCompra = tables.current.ultima_compra;

          const hashUltimaCompra = catalogUltimaCompra?.reduce(
            (acc: { [x: string]: any }, curr: { SEQPESSOA: number; SEQPRODUTO: string | number }) => {
              if (curr.SEQPESSOA === client) {
                acc[curr.SEQPRODUTO] = curr;
              }
              return acc;
            },
            {} as Record<number, any>
          );

          const updatedProducts = newCatalog.map((p) => ({
            ...p,
            ultimaCompra: hashUltimaCompra ? hashUltimaCompra[p.COD_PRODUTO] || null : null,
          }));

          const currentCatalog = updatedProducts.filter((p) => !restrictions[p.COD_PRODUTO]);
          pruneCalculatedCache(client);

          const prevClientScopes = prevCatalog[client] || {};
          let nextClientScopes = { ...prevClientScopes, [scopeKey]: currentCatalog };

          const maxScopes = lowMemoryCatalogMode.current ? MAX_CATALOG_SCOPES_LOW_MEMORY : MAX_CATALOG_SCOPES_DEFAULT;
          const scopeKeys = Object.keys(nextClientScopes);
          if (scopeKeys.length > maxScopes) {
            const keysToDrop = scopeKeys.filter((k) => k !== scopeKey).slice(0, scopeKeys.length - maxScopes);
            keysToDrop.forEach((k) => {
              delete nextClientScopes[k];
              delete isLoaded.current[`${client}:${k}`];
            });
          }

          if (!lowMemoryCatalogMode.current) {
            return { ...prevCatalog, [client]: nextClientScopes };
          }

          Object.keys(prevCatalog).forEach((key) => {
            const codClient = Number(key);
            if (codClient !== client) {
              Object.keys(isLoaded.current).forEach((loadKey) => {
                if (loadKey.startsWith(`${codClient}:`)) {
                  delete isLoaded.current[loadKey];
                }
              });
            }
          });

          const nextCalculated = {} as Record<string, PriceProductProps[]>;
          Object.entries(calculatedList.current).forEach(([key, value]) => {
            if (key.startsWith(`${client}-`)) {
              nextCalculated[key] = value;
            }
          });
          calculatedList.current = nextCalculated;

          return { [client]: nextClientScopes };
        } finally {
          isLoadingProducts.current = false;
        }
      });
    },
    [tables, pruneCalculatedCache]
  );

  const resetCatalogCachesAfterSync = useCallback(() => {
    setHookProducts({} as CatalogByClientAndScope);
    isLoaded.current = {};
    calculatedList.current = {};
  }, []);

  /** Invalidação adiada: home (`MAIN`) ou troca de `COD_CLIENTE`. */
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(RCA_CATALOG_PENDING_INVALIDATE_SESSION_KEY);
      if (parseCatalogPendingInvalidateFlag(raw) !== RCA_CATALOG_PENDING_DEFERRED) return;

      const cod = selectedClient?.COD_CLIENTE;
      const prev = prevClientCodRef.current;
      const clientChanged =
        prev !== undefined && cod !== undefined && prev !== cod;
      prevClientCodRef.current = cod;

      const isHome =
        pathname === MAIN.link ||
        pathname === ECOMMERCE.link ||
        pathname === '/' ||
        pathname === '';

      if (clientChanged || isHome) {
        sessionStorage.removeItem(RCA_CATALOG_PENDING_INVALIDATE_SESSION_KEY);
        resetCatalogCachesAfterSync();
      }
    } catch {
      /* ignore */
    }
  }, [pathname, selectedClient?.COD_CLIENTE, resetCatalogCachesAfterSync]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(RCA_CATALOG_PENDING_INVALIDATE_SESSION_KEY);
      if (parseCatalogPendingInvalidateFlag(raw) === RCA_CATALOG_PENDING_IMMEDIATE) {
        sessionStorage.removeItem(RCA_CATALOG_PENDING_INVALIDATE_SESSION_KEY);
        resetCatalogCachesAfterSync();
      }
    } catch {
      /* ignore */
    }
  }, [resetCatalogCachesAfterSync]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<CatalogInvalidateEventDetail>).detail;
      // Com `deferMemoryUntilSafe`, o flush é feito ao ir à home ou trocar de cliente.
      // Sem defer: invalidação imediata — limpa memória e remove pending na sessão.
      if (detail?.deferMemoryUntilSafe) {
        return;
      }
      resetCatalogCachesAfterSync();
      try {
        sessionStorage.removeItem(RCA_CATALOG_PENDING_INVALIDATE_SESSION_KEY);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener(RCA_CATALOG_INVALIDATE_EVENT, handler as EventListener);
    return () => window.removeEventListener(RCA_CATALOG_INVALIDATE_EVENT, handler as EventListener);
  }, [resetCatalogCachesAfterSync]);

  return (
    <LoadingContext.Provider value={{ catalog, setProducts, calculatedList, isLoading, isLoaded }}>
      {children}
    </LoadingContext.Provider>
  );
};

const usePrices = () => {
  const context = useContext(LoadingContext);

  if (!context)
    throw new Error('O context useTables só pode ser usado dentro de seu escopo. Erro ao usar hook useTables');

  return context;
};

export { PricesProvider, usePrices };
